import AddCircleIcon from "@mui/icons-material/AddCircle";
import {
  Box,
  Button,
  InputAdornment,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNotification } from "app/NotificationProvider";
import DetailDialog from "common/components/DetailDialog";
import {
  AnimalGroup,
  getAllFieldsForAnimalGroupIdFromArray,
  getAvailableDryMatterForAnimalGroupIdFromArray,
  getDemandFromArray,
  useCurrentFarmStockClasses,
  useFields,
  useUpdateAnimalGroup,
} from "model";

import { styled } from "@mui/material/styles";
import { useTranslation } from "common/locales";
import { AssignFieldDialog } from "features/animalGroupDetail";
import { GrassWedge } from "features/grassWedge";
import { sumBy } from "lodash";
import { useActiveFeedBudgetDemand } from "model/useActiveFeedBudgetDemand";
import { useEffect, useMemo, useState } from "react";
import { useUIStore } from "stores";
import { AnalyticsEvents, analytics } from "../../common/analytics";

const BELLFLOWER_COLOR = "#D3C9E7";
const MAX_CONTENT_WIDTH = 900;

export default function GrassWedgeDialog({
  open,
  close,
  animalGroup,
}: GrassWedgeProps) {
  useEffect(() => {
    if (open) {
      analytics.track(AnalyticsEvents.GRASS_WEDGE_VIEWED);
    }
  }, [open]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { guid, name } = animalGroup;
  const [assignFieldDialogOpen, setAssignFieldDialogOpen] = useState(false);
  const growthRate = useUIStore((state) => state.growthRate) || 0;

  const [postGrazingTarget, setPostGrazingTarget] = useState(
    String(animalGroup.postGrazingTarget ?? "1500")
  );
  const [rotationLength, setRotationLength] = useState(
    String(animalGroup.rotationLength ?? "30")
  );

  // Use React Query for fields data
  const { data: allFields = [] } = useFields();
  const fields = useMemo(
    () => getAllFieldsForAnimalGroupIdFromArray(allFields, guid),
    [allFields, guid]
  );

  // Use React Query hooks for stock classes and mutation
  const { data: stockClasses } = useCurrentFarmStockClasses();
  const animalGroupStockClasses = useMemo(
    () => stockClasses.filter((sc) => sc.animalGroup === guid),
    [stockClasses, guid]
  );
  // Demand source must match the rest of the app (animal group cards, field
  // details, chart tooltip): prefer the active feed budget plan, fall back to
  // backend stock classes only when no plan covers this group.
  const activeBudgetDemand = useActiveFeedBudgetDemand();
  const demand =
    activeBudgetDemand.get(guid) ?? getDemandFromArray(animalGroupStockClasses);

  const { t } = useTranslation();

  const updateAnimalGroupMutation = useUpdateAnimalGroup();

  const { notify } = useNotification();

  const totalAreaHectar = sumBy(fields, "area") / 10000;

  const totalAvailableDM = useMemo(
    () =>
      Math.round(
        getAvailableDryMatterForAnimalGroupIdFromArray(fields, animalGroup)
      ),
    [fields, animalGroup]
  );

  let averageCover = 0,
    grazingDaysAhead = 0,
    preGrazingTarget = 0,
    demandPerHaPerDay = 0;

  if (totalAreaHectar > 0) {
    averageCover = Math.round(
      fields.reduce(
        (prev, curr) => prev + (curr.dryMatterNow * curr.area) / 10000,
        0
      ) / totalAreaHectar
    );

    grazingDaysAhead = demand
      ? Math.floor(totalAvailableDM / Number(demand))
      : Infinity;

    demandPerHaPerDay = demand
      ? Math.round(Number(demand) / totalAreaHectar)
      : 0;

    preGrazingTarget = Math.round(
      ((demand || 0) * Number(rotationLength)) / totalAreaHectar +
        Number(postGrazingTarget)
    );
  }

  const dialogTitle = (
    <>
      <Box component="span" sx={{ color: "text.secondary", fontWeight: 400 }}>
        {t("grassWedge.title")}:{" "}
      </Box>
      {name}
    </>
  );

  return (
    <DetailDialog
      open={open}
      onClose={close}
      title={dialogTitle}
      contentSx={{ backgroundColor: "#F7FCF1" }}
    >
      <ContentContainer>
        {/* Metrics Card */}
        <Card>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              gap: { xs: 0, sm: 3 },
            }}
          >
            <MetricItem>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  fontSize: "0.7rem",
                }}
              >
                {t("grassWedge.averageCover")}
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#1a1a2e" }}
              >
                {averageCover}{" "}
                <span style={{ fontSize: "0.75rem", color: "#666" }}>
                  kgDM/ha
                </span>
              </Typography>
            </MetricItem>
            <MetricItem>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  fontSize: "0.7rem",
                }}
              >
                {t("grassWedge.growth")}
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#1a1a2e" }}
              >
                {growthRate ? Math.round(growthRate) : "-"}{" "}
                <span style={{ fontSize: "0.75rem", color: "#666" }}>
                  kgDM/ha/day
                </span>
              </Typography>
            </MetricItem>
            <MetricItem>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  fontSize: "0.7rem",
                }}
              >
                {t("grassWedge.demand")}
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#1a1a2e" }}
              >
                {demandPerHaPerDay}{" "}
                <span style={{ fontSize: "0.75rem", color: "#666" }}>
                  kgDM/ha/day
                </span>
              </Typography>
            </MetricItem>
            <MetricItem>
              <Typography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  display: "block",
                  fontSize: "0.7rem",
                }}
              >
                {t("grassWedge.grazingDaysAhead")}
              </Typography>
              <Typography
                variant="body1"
                sx={{ fontWeight: 600, color: "#1a1a2e" }}
              >
                {grazingDaysAhead === Infinity ? "∞" : grazingDaysAhead}{" "}
                <span style={{ fontSize: "0.75rem", color: "#666" }}>days</span>
              </Typography>
            </MetricItem>
          </Box>
        </Card>

        {/* Settings Card */}
        <Card>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: "text.secondary", fontWeight: 600 }}
            >
              {t("grassWedge.settings")}
            </Typography>
            <Button
              startIcon={<AddCircleIcon sx={{ fontSize: "16px !important" }} />}
              size="small"
              onClick={() => setAssignFieldDialogOpen(true)}
              sx={{
                color: "primary.main",
                textTransform: "none",
                fontWeight: 600,
                fontSize: "0.8rem",
                padding: "4px 8px",
                minWidth: "auto",
                "&:hover": {
                  backgroundColor: "rgba(56, 142, 60, 0.08)",
                },
              }}
            >
              {t("grassWedge.assignField")}
            </Button>
          </Box>
          <StyledForm onSubmit={saveAnimalGroup}>
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                gap: 2,
              }}
            >
              <StyledTextField
                variant="outlined"
                size="small"
                onBlur={saveAnimalGroup}
                value={rotationLength}
                onChange={(event) => setRotationLength(event.target.value)}
                type="number"
                label={t("grassWedge.rotationLength")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {t("units.days")}
                    </InputAdornment>
                  ),
                }}
              />
              <StyledTextField
                variant="outlined"
                size="small"
                onBlur={saveAnimalGroup}
                value={postGrazingTarget}
                onChange={(event) => setPostGrazingTarget(event.target.value)}
                type="number"
                label={t("grassWedge.postGrazingTarget")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {t("units.kilogramPerHectar")}
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  step: 100,
                }}
              />
              <StyledTextField
                variant="outlined"
                size="small"
                value={preGrazingTarget}
                disabled
                type="number"
                label={t("grassWedge.preGrazingTarget")}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      {t("units.kilogramPerHectar")}
                    </InputAdornment>
                  ),
                }}
                inputProps={{
                  step: 100,
                }}
              />
            </Box>
            <input type="submit" hidden />
          </StyledForm>
        </Card>

        {/* Empty/Single Field Warning */}
        {fields.length <= 1 && (
          <Box
            sx={{
              backgroundColor: "#FDFBEB",
              border: "1px solid #F7E880",
              borderRadius: 2,
              p: 2,
              mb: 2,
              textAlign: "center",
            }}
          >
            <Typography
              variant="body1"
              sx={{ color: "#A48137", fontWeight: 500 }}
            >
              {t(
                fields.length === 0
                  ? "grassWedge.noFields.prompt"
                  : "grassWedge.singleField.prompt"
              )}
            </Typography>
          </Box>
        )}

        {/* Chart Card */}
        <ChartCardContainer>
          <GrassWedge
            fields={fields}
            preGrazingTarget={Number(preGrazingTarget)}
            postGrazingTarget={Number(postGrazingTarget)}
          />
        </ChartCardContainer>
      </ContentContainer>
      <AssignFieldDialog
        animalGroupId={animalGroup.guid}
        open={assignFieldDialogOpen}
        close={() => setAssignFieldDialogOpen(false)}
      />
    </DetailDialog>
  );

  async function saveAnimalGroup(e?: React.FormEvent<HTMLFormElement>) {
    e?.preventDefault();
    try {
      await updateAnimalGroupMutation.mutateAsync({
        ...animalGroup,
        rotationLength: Number(rotationLength),
        postGrazingTarget: Number(postGrazingTarget),
      });
      analytics.track(AnalyticsEvents.GRASS_WEDGE_UPDATED);
      notify(t("grassWedge.notification.saved"), "success");
    } catch (e) {
      notify(t("grassWedge.notification.error"), "error");
    }
  }
}

type GrassWedgeProps = {
  open: boolean;
  close: () => void;
  animalGroup: AnimalGroup;
};

const StyledForm = styled("form")({
  width: "100%",
});

const ContentContainer = styled("div")({
  maxWidth: `${MAX_CONTENT_WIDTH}px`,
  margin: "0 auto",
  width: "100%",
});

const Card = styled("div")({
  backgroundColor: "white",
  borderRadius: "16px",
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.08)",
  padding: "16px",
  marginBottom: "16px",
  width: "100%",
  boxSizing: "border-box",
});

const ChartCardContainer = styled("div")({
  display: "flex",
  justifyContent: "center",
  width: "100%",
});

const StyledTextField = styled(TextField)({
  flex: 1,
  minWidth: "180px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#f8f8f8",
    "&:hover .MuiOutlinedInput-notchedOutline": {
      borderColor: BELLFLOWER_COLOR,
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      borderColor: "#7d5da3",
    },
  },
  "& .MuiInputLabel-root": {
    fontSize: "0.85rem",
  },
});

const MetricItem = styled("div")(({ theme }) => ({
  padding: "8px 0",
  borderBottom: "1px solid #f0f0f0",
  flex: 1,
  "&:last-child": {
    borderBottom: "none",
  },
  "@media (min-width: 600px)": {
    borderBottom: "none",
    borderRight: "1px solid #f0f0f0",
    padding: "0 16px",
    "&:first-of-type": {
      paddingLeft: 0,
    },
    "&:last-child": {
      borderRight: "none",
      paddingRight: 0,
    },
  },
}));
